import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { exportFlashCardsToPDF } from "./exportFlashCardsPDF";

const DownloadFlashCardsModal = ({ isOpen, toggle, currentOutlet }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchFlashcards();
    } else {
      setFlashcards([]);
      setSelectedIds([]);
    }
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [isOpen]);

  const fetchFlashcards = async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    try {
      const res = await postDataAPI(
        {
          serviceName: "item_data",
          op: "server_coaching_outlet_items",
          display_classcification: "Yes",
          type: "Flash",
          status: "Active",
          is_show_all: true,
          course_id: "",
          flash_status: "Complete",
        },
        controller.signal,
      );
      if (res.status === "ok") {
        const cards = res.data.server_data || [];
        setFlashcards(cards);
        // By default, select all flashcards
        const allIds = cards.map((c) => Number(c.flash_id)).filter(Boolean);
        setSelectedIds(allIds);
      } else {
        alert(res.message || "Failed to load flashcards");
      }
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        console.log("Fetch flashcards request cancelled");
        return;
      }
      console.error(err);
      alert("Failed to load flashcards");
    } finally {
      if (controllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = flashcards.map((c) => Number(c.flash_id)).filter(Boolean);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectCard = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleDownload = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one flashcard to download.");
      return;
    }

    setDownloading(true);
    try {
      const res = await postDataAPI({
        serviceName: "item_data",
        op: "fetch_all_flash_cards_detail_data",
        selectedOutletID: currentOutlet?.id,
        flash_cards: selectedIds,
      });

      if (res.status !== "ok") {
        alert(res.message || "Failed to fetch flashcards for download");
        return;
      }

      const cards = Array.isArray(res.data) ? res.data : [];
      if (!cards.length) {
        alert("No flashcard details found to download");
        return;
      }

      const outletLabel = (
        currentOutlet?.name ||
        currentOutlet?.outlet_name ||
        "Outlet"
      )
        .toString()
        .replace(/[^\w\- ]+/g, "")
        .trim()
        .replace(/\s+/g, "_");
      const fileName = `FlashCards_${outletLabel || "All"}.pdf`;

      await exportFlashCardsToPDF(cards, fileName);
      toggle(); // Close modal on success
    } catch (err) {
      console.error(err);
      alert("Failed to generate flashcards PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const isAllSelected =
    flashcards.length > 0 && selectedIds.length === flashcards.length;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Download Flash Cards</ModalHeader>
      <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading flashcards...</p>
          </div>
        ) : flashcards.length === 0 ? (
          <p className="text-center text-muted">No flashcards found.</p>
        ) : (
          <div>
            <div className="form-check mb-3 pb-2 border-bottom">
              <Input
                type="checkbox"
                id="select-all-flashcards"
                checked={isAllSelected}
                onChange={handleSelectAll}
                className="form-check-input"
                style={{ cursor: "pointer" }}
              />
              <Label
                htmlFor="select-all-flashcards"
                className="form-check-label fw-bold"
                style={{ cursor: "pointer" }}
              >
                Select All ({flashcards.length})
              </Label>
            </div>

            <div className="flashcards-list">
              {flashcards.map((card) => {
                const cardId = Number(card.flash_id);
                return (
                  <div key={card.item_id || cardId} className="form-check mb-2">
                    <Input
                      type="checkbox"
                      id={`flashcard-${cardId}`}
                      checked={selectedIds.includes(cardId)}
                      onChange={(e) =>
                        handleSelectCard(cardId, e.target.checked)
                      }
                      className="form-check-input"
                      style={{ cursor: "pointer" }}
                    />
                    <Label
                      htmlFor={`flashcard-${cardId}`}
                      className="form-check-label"
                      style={{ cursor: "pointer" }}
                    >
                      {card.flash_card_name || card.item_name}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          outline
          color="secondary"
          onClick={toggle}
          disabled={downloading}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleDownload}
          disabled={loading || downloading || selectedIds.length === 0}
        >
          {downloading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Downloading...
            </>
          ) : (
            `Download (${selectedIds.length})`
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DownloadFlashCardsModal;
